import { AppUser } from './AppUser';
import { IReferential, ITranslation } from './IReferential';
import { Organization, OrganizationBankAccount, OrganizationInfoDto } from './Organization';
import {
  Workflow,
  WorkflowCalculation,
  WorkflowOptimization,
  WorkflowPartner,
  WorkflowSectionStepForm,
  WorkflowSupplier
} from './Workflow';
import { Tariff, TariffType } from './Tariff';
import { Vehicle as VehicleExt, VehicleOffer } from './Vehicle';
import { OrderPart, OrderPartDTO } from './Order';
import { UserNotification } from './UserNotificationsUpdate';
import { ReportParameter } from './Reports';
import { AppActionCreators, AppState, CaseSettingsState } from '../store';
import { RouteComponentProps } from 'react-router';

export interface Notice {
  id: number;
}

export interface CaseFilters {
  caseTypeIds: number[];
  statusIds: number[];
  workflowIds: number[];
  assignedToList: string[];
  createdByList: string[];
  partnerIds: number[];
  organizationIds: number[];
  isInternal: boolean | null;
  page: number;
  pageSize: number | null;
  text: string;
  isNew: boolean;
  dateFilterCode: string;
  dateFilterStartDate: Date | null;
  datefilterEndDate: Date | null;
  vehicleIds: number[];
  policyTypeIds: number[];
  vinSearchText: string;
  plateNumberSearchText: string;
  caseNumberSearchText: string;
  countryCode: string | null;
  isAssignedToId: boolean;
  makeSearchText: string;
  modelSearchText: string;
  organizationOwnerId: number | null;
}

export interface CaseFiltersResult {
  caseTypes: IReferential[];
  caseStatuses: IReferential[];
  workflowIds: number[];
  assignedToList: string[];
  createdByList: string[];
  partnerIds: number[];
  organizationIds: number[];
  casePolicyTypes: IReferential[];
}

export interface CaseList {
  cases: Case[];
  total: number;
  page: number;
}

export interface CaseListDto {
  Cases: Case[];
  total: number;
  page: number;
}

export interface Case {
  id: number;
  caseTypeId: number;
  caseType: IReferential | null;
  caseNumber: number;
  organizationOwnerId: number;
  organizationOwner: Organization | null;
  createdBy: string;
  createdByUser: AppUser | null;
  dateCreation: Date;
  caseWorkflow: CaseWorkflowHistory;
  caseTariff: CaseTariffHistory;
  caseStatus: CaseStatusHistory;
  caseParameters: CaseParameterHistory[];
  casePartners: CasePartnerHistory[];
  caseVehicle: CaseVehicleHistory;
  caseEvent: CaseEventHistory;
  caseValuation: CaseValuationHistory;
  caseClient: CaseClient;
  caseCalculation: CaseCalculationHistory;
  caseCarDetailsExtended: CarDetails;
  vehicleId: number | null;
  vehicle: VehicleExt | null;
  caseVehicleDetails: CaseVehicleDetails;
  lastOfferRejected: boolean;
  carInService: boolean;
  carInServiceDate: Date | null;
  carInServiceModifiedBy: string | null;
  hasRemainingNonStockParts: boolean;
  jobTypeId: number | null;
  approvedCalculationId: number | null;
  invoiceId: number | null;
  isOffline: boolean;
  isInvoicedByGTM: boolean;
  skipGeneratingDocuments: boolean;
  isGtmManaged: boolean;
  alternativePartVariantsEnabled: boolean;
  partnerOrganizationId: number | null;
  serviceId: number | null;
}

export interface CaseByVehicleIdSearchResult {
  id: number;
  vehicleId: number;
  workflowId: number;
  caseStatusId: number;
  caseStatus: CaseInfoResult;
  caseType: CaseInfoResult;
  caseParameters: CaseParameterHistory[];
}

export interface CaseInfoResult {
  code: string;
  id: number;
}

export interface CaseComment {
  id: number;
  caseId: number;
  case: Case;
  text: string;
  userId: string;
  user: AppUser | null;
  date: Date;
  commentTypeCode: string;
  isFromSupportTab: boolean;
}

export interface CaseStatusHistory {
  id: number;
  caseId: number;
  caseStatusId: number;
  caseStatus: IReferential | null;
  date: Date;
  userId: string;
  user: AppUser | null;
  assignedToId: string;
  assignedTo: AppUser | null;
  assignedToHoId: number;
  isNew: boolean;
  isViewed: boolean;
}

export interface ChangeCaseStatusRequest {
  caseId: number;
  statusId: number;
}

export interface CaseWorkflowHistory {
  id: number;
  caseId: number;
  workflowId: number;
  workflowDisplayName: string;
  date: Date;
  userId: number;
  user: AppUser | null;
}

export interface CasePartnerHistory {
  id: number;
  caseId: number;
  caseWorkflowHistoryId: number;
  organizationId: number;
  organization: Organization | null;
  date: Date;
  userId: string;
  user: AppUser | null;
}

export interface CaseParameterHistory {
  id: number;
  caseId: number;
  caseWorkflowHistoryId: number;
  parameterId: number;
  parameter: IReferential | null;
  value: string;
  date: Date;
  userId: number;
  user: AppUser | null;
  parameterCode: string | null;
}

export interface CaseTariffHistory {
  id: number;
  caseId: number;
  caseWorkflowHistoryId: number;
  tariffId: number;
  tariff: Tariff | null;
  date: Date;
  userId: number;
  user: AppUser | null;
  caseTariffValuesHistory: CaseTariffValueHistory[];
}

export interface CaseTariffValueHistory {
  id: number;
  startVehicleYear: number | null;
  endVehicleYear: number | null;
  caseTariffHistoryId: number;
  tariffTypeId: number;
  tariffType: TariffType | null;
  value: number;
  offerValue: number;
  brandId: number | null;
  brand: IReferential | null;
  vehicleTypeId: number | null;
  vehicleType: IReferential | null;
  jobTypeId: number | null;
}

export interface CaseAttachmentDto {
  id: number;
  caseId: number | null;
  attachmentTypeId: number;
  attachmentTypeCode?: string | null;
  fileName: string;
  originalFileName: string;
  files: File[];
  caseRequestId: number | null;
  paymentId?: number | null;
  caseServiceAppointmentId?: number | null;
  caseClientId?: number | null;
  isSelection: boolean;
  fileUrl?: string;
  formId?: number | null;
  orderStatusHistoryId?: number | null;
}

export interface CaseAttachment {
  id: number;
  caseId: number;
  attachmentTypeId: number;
  attachmentType: IReferential | null;
  fileName: string;
  uploadDate: Date;
  carPhotos: boolean;
  originalFileName: string;
  file: File;
  caseRequestId: number | null;
  paymentId: number | null;
  serviceAppointmentId: number | null;
  caseClientId: number | null;
  isSelection: boolean;
  url?: string;
  appointmentId?: number | null;
  includeInOtherTab?: boolean;
  checkFileName?: boolean;
  invoiceCurrency?: string;
  amount?: number;
  remainingAmount?: number;
  calculationId?: number;
  invoiceId?: number | null;
  invoice?: InvoiceResponseDto | null;
  isHiddenFromClientApp?: boolean;
  isHiddenFromServiceApp?: boolean;
  attachmentCategoryId: number | null;
  attachmentCategory: IReferential | null;
  damagePartId: number | null;
  damagePart: DamagePart | null;
  distanceTypeId: number | null;
  distanceType: IReferential | null;
  attachmentStatusId: number | null;
  attachmentStatus: IReferential | null;
  pointX: number | null;
  pointY: number | null;
}

export interface CaseSelectedAttachment {
  id: number;
  fileName: string;
  fileInputKey?: number;
}
export interface CaseAttachments {
  caseId: number;
  caseTypeDisplayName: string;
  organizationOwnerId: number;
  organizationOwnerName: string;
  attachments: CaseAttachment[];
  picturesExpanded?: boolean;
  documentsExpanded?: boolean;
}

export interface CaseCalculationHistory {
  id: number;
  caseId: number;
  calculationTypeId: number;
  calculationType: IReferential;
  date: Date;
  userId: string;
  user: AppUser | null;
  currencyId: number;
  currency: IReferential | null;
  currencyCode: string;
  amount: number;
  amountPieces: number;
  smallPartsValue: number;
  smallPartsPercent: number;
  amountLabor: number;
  amountPaintingMaterials: number;
  amountPaintingLabor: number;
  amountPiecesWithDiscount: number;
  amountPiecesDiscount: number;
  amountLaborWithDiscount: number;
  amountPaintingMaterialsWithDiscount: number;
  amountPaintingLaborWithDiscount: number;
  amountDiscount: number;
  amountWithoutTVA: number;
  amountTVA: number;
  comment: string;
  offerNumber: string;
  calculationSource: string;
  partsLocationOrganizationId: number | null;
  partsOrganizationId: number | null;
  organizationId: number | null;
  organizationName: string | null;
  vinQuery: boolean;
  externalCalculation: boolean;
  insurerCalculation: boolean;
  manipulationPercent: number;
  manipulationValue: number;
  prcApproved: boolean;
  formId: number;
  additionPercent: boolean;
  amountAddition: number;
  amountAdditionPieces: number;
  amountAdditionLabor: number;
  additionValue: number;
  parentId: number | null;
  additionInsurer: number | null;
  additionInsurerParts: number;
  maintenancePackageId: number | null;
  hasBeenApproved: boolean;
  smallPartsAquisitionValue: number;
  interogatePartner: boolean;
  calculationXMLFileName: string | null;
  calculationPDFFileName: string | null;
  hasUnorderedParts: boolean;
  isGTMCalculation: boolean;
  serviceCalculationId: number | null;
  statusId: number | null;
  status: IReferential | null;
  statusDate: Date | null;
}

export interface CaseVehicleHistory {
  id: number;
  caseId: number;
  vin: string | undefined;
  plateNumber: string | undefined;
  firstPlateDate: string | undefined;
  registrationDate: string | undefined;
  motDate: string | null;
  carColor: string | undefined;
  numberOfKilometers: number | undefined;
  carCategoryId: number | undefined;
  carClassId: number | undefined;
  power: number | undefined;
  gearboxType: string | undefined;
  engineSize: number | undefined;
  brandId: number | undefined;
  modelId: number | undefined;
  model: Model | null;
  date: Date;
  userId: number;
  user: AppUser | null;
  engineCode: string;
  identifiedMake: string;
  identifiedModel: string;
  identifiedCarBody: string;
  identifiedFuelType: string;
  vehicleTypeId: number | null;
  vehicleType: IReferential | null;
  [key: string]: any;
}

export interface CaseEventHistory {
  id: number;
  caseId: number;
  policyTypeId: number;
  insuredPersonTypeId: number;
  insuredPersonType: IReferential | null;
  insuredName: string;
  companyNumber: string;
  companyName: string;
  pin: string;
  caseNumber: string;
  eventDate: string;
  noticeDate: string;
  claimReviewDate: string;
  eventPlace: string;
  driverName: string;
  phoneDriver: string;
  companyPlateNumber: string;
  address: string;
  iban: string;
  date: Date;
  userId: number;
  user: AppUser | null;
  policyNumber: string;
  serviceContractNumber: string;
  offerNumber: string;
  organizationId: number;
  deliveryOrganizationId: number;
  insurerName: string;
  interventionTypeId: number | null;
  interventionType: IReferential | null;
  regionId: number | null;
  [key: string]: any;
}

export interface Vehicle {
  id: number;
  vin: string;
  model: string;
  make: string;
  firstRegistrationDate: Date;
  manufacturingDate: Date;
}

export interface VehicleDto {
  lastCase: VehicleCaseDto;
  id: number;
  vin: string;
  plateNumber: string;
  model: string;
  make: string;
  numberOfKilometers?: number;
  organizationInterfaceCode: string;
  organizationOwnerId: number;
  status: VehicleStatusDto;
  firstPlateDate?: Date;
  class?: string;
  region?: string;
  groupamaPret: string;
  grawePret: string;
  proiectrcaPret: string;
  hellasPret: string;
  allianzPret: string;
  atmManufactureYear: number | null;
  atmEnginePower: number | null;
  atmEngineCapacity: number | null;
  atmFuelType: string | null;
}

export interface VehicleCaseDto {
  caseTypeCode: string;
  caseTypeId: number;
  caseTypeNameLocalized: string;
  createdById: string;
  createdByUserName: string;
  dateCreation: Date;
  id: number;
}

export interface VehicleStatusDto {
  code: string;
  date: Date;
  id: number;
  nameLocalized: string;
  userId: string;
  userName: string;
}

export interface ClientDto {
  id: number;
  personTypeId: number | null;
  personType: IReferential | null;
  companyNumber: string;
  companyName: string;
  pin: string;
  lastName: string;
  firstName: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  region: string;
  bankAccount: string;
  district: string | null;
}

export interface VehicleEquipment {
  id: number;
  vehicleId: number;
  vehicle: Vehicle | null;
  name: string;
  date: Date;
}

export interface CaseCalculationPart {
  id: number;
  caseCalculationHistoryId: number;
  calculationSectionId: number;
  calculationSection: IReferential | null;
  name: string;
  pieceNr: number;
  guideNumber: string;
  originalGuideNumber: string | null;
  carPartCategoryId: number | null;
  carPartCategory: IReferential | null;
  originalPrice: number;
  supplier: Organization | null;
  supplierId: number | null;
  deliveryTime: string | null;
  validityTime: string | null;
  codeType: string | null;
  excludeFromOrder: boolean | null;
  orderCode: string | null;
  type: string | null;
  locationId: number | null;
  producer: string | null;
  alternativeGuideNumber: string | null;
  alternativePartCode: string | null;
  alternativePartQualityId: number | null;
  alternativePartQuality: IReferential | null;
  alternativePrice: number | null;
  alternativePartStockQuantity: number | null;
  alternativePartStockLocation: string | null;
  alternativePartName: string | null;
  alternativePartDiscount: number | null;
  discount: number;
  remarks: string | null;
  alternativePartRemarks: string | null;
  amount: number;
  solutionValueId: number | null;
  solutionValue: IReferential | null;
  solutionComment: string;
  repairMethod: string;
  minQty: number;
  packing: string | null;
  bundleItem: string | null;
  bundleNo: string | null;
  bundleQuantity: string | null;
  package: any;
  isOrdered: boolean;
  isChild: boolean;
  productId: number | null;
  codEan: string | null;
  groupedUniqueId: string | null;
  additionValue: number;
  additionFixedValue: number;
  alternativeNetPrice: number | null;
  alternativePriceWithoutDiscount: number | null;
  additionInsurer: number | null;
  totalAmount: number;
  isOwnStock: boolean;
  auxiliarInformation: string | null;
  editOriginalPrice: boolean;
  isNew: boolean;
  alternativePartDescription: string | null;
  availableQuantity: number | null;
  isInStock: boolean;
  supportComment: string | null;
  partStockId: number | null;
  expectedDeliveryDate: string | null;
  caseCalculationPartInterogations: CaseCalculationPartInterogation[];
  manualGTModified: boolean;
  serviceCalculationPartId: number | null;
  suggestedVariantsJson: string | null;
  isServiceRecommendationChanged: boolean;
  isLoading: boolean;
  hasBeenInterogated: boolean;
  alternativeParts: CaseCalculationAlternativePart[];
  isExpanded: boolean;
  unitsError: boolean;
  priceError: boolean;
  descriptionError: boolean;
  damagePartId: number | null;
}

export interface CaseCalculationPartInterogation {
  interogationList: string | null;
}

/** Hierarchical delivery-address node (organization branch tree) for the "Adresă de livrare" select. */
export interface OrganizationBranch {
  id: number;
  organizationId: number;
  organizationName: string;
  organizationAddress: string;
  children: OrganizationBranch[];
  parentId: number | null;
  path: string | null;
  label: string;
  checked: boolean | null;
}

export interface CaseCalculationAlternativePart {
  id: number;
  caseCalculationHistoryId: number;
  name: string;
  alternativeGuideNumber: string;
  alternativePartCode: string;
  alternativePrice: number;
  alternativePartDiscount: number | null;
  alternativePartQualityId: number;
  alternativePartQuality: IReferential | null;
  deliveryTime: string | null;
  validityTime: string | null;
  codeType: string | null;
  orderCode: string | null;
  type: string | null;
  locationId: number | null;
  excludeFromOrder: boolean | null;
  producer: string;
  supplier: Organization | null;
  supplierId: number | null;
  originalPartId: number;
  selectedAlternativePart: number;
  availableQuantity: number | null;
  isInStock: boolean;
  stockLocation: string;
  remarks: string;
  minQty: number;
  packing: string | null;
  bundleItem: string | null;
  bundleNo: string | null;
  bundleQuantity: string | null;
  partFromGroup: number;
  additionalInfo: string | null;
  quantityAdditionalInfo: string | null;
  isChild: boolean;
  productId: number | null;
  codEan: string | null;
  expectedDeliveryDate: string | null;
  groupedUniqueId: string | null;
  alternativeNetPrice: number | null;
  alternativePriceWithoutDiscount: number | null;
  additionInsurer: number | null;
  alternativePartQualityName: string | null;
  supplierName: string | null;
  partStockId: number | null;
  isOwnStock: boolean;
  isSelectedAlternativePart: boolean;
  isVariantSelected: boolean;

  // Service's favorite variant, set in ClientApp.
  isServiceRecommendation: boolean;

  // The variant the client (fleet) picked, stamped by ClientApp2 on send.
  isClientSelection: boolean;
}
export interface CaseCalculationSupplierAlternativePart {
  prevVal: OrderPartDTO;
  newVal: CaseCalculationAlternativePart;
}
export interface Model {
  isActive: boolean;
  dateModification: string | null;
  modifiedBy: string | null;
  gtId: number | null;
  id: number;
  code: string;
  brand: string;
  brandId: number;
  name: string;
  displayName: string;
  carClass: IReferential | null;
  carClassId: number | null;
  description: string;
}
export interface ErrorInfo {
  type: string;
  code: string;
  description: string;
}

export interface VinDecError {
  errorInfo: ErrorInfo;
}
export interface ModelData {
  makeCode: string;
  makeDescription: string;
  modelDescription: string;
  umc: string;
}

export interface VinDecodeResponse {
  error: VinDecError;
  responseInfo: {
    vehicleInfoDec: {
      modelList: {
        modelData: ModelData[];
      };
      vin: string;
    };
  };
}

export interface BackToServiceForm {
  id: number;
  caseId: number;
  commentId: number;
  comment: CaseComment | null;
  attachments: CaseAttachment[];
  organizationId: number;
  organization: WorkflowPartner | null;
  userId: number;
  user: AppUser | null;
}

export interface CaseRequest {
  id: number;
  caseId: number;
  to: string;
  cc: string;
  subject: string;
  date: Date;
  text: string;
  user: AppUser | null;
  userId: string;
  userIdTo: string;
  attachments: CaseAttachment[];
  caseSectionStepFormId: number;
  organizationId: number | null;
  hasOffer: boolean | null;
  hasInvoice: boolean | null;
  calculationId: number | null;
  caseClientHistoryId: number | null;
  vehicleId: number;
  gatewayServiceUrl: string | undefined | null;
  offerId: number;
  userOrganizationId: number | undefined | null;
  isVehicleCost: boolean | null;
  costType: IReferential | null;
  value: number | null;
  currencyRate: number | null;
  currency: string | null;
  secondValue: number | null;
  secondCurrency: string | null;
  clientEmail: string;
  isExternal: boolean;
  organizationOwnerId: number | null;
  hideOrganizationLogo: boolean | null;
}

export interface CasePaymentHistory {
  id: number;
  caseId: number;
  comment: string;
  attachments: CaseAttachment[];
  organizationId: number;
  organization: WorkflowPartner | null;
  userToId: string;
  userTo: AppUser | null;
  paymentTypeId: number | null;
  paymentType: IReferential | null;
  paymentRequestNoId: number | null;
  paymentRequestNo: IReferential | null;
  paymentAmount: number | null;
  paymentAmountWithoutVAT: number | null;
  secondPaymentAmount: number | null;
  secondPaymentAmountWithoutVAT: number | null;
  secondCurrency: string | null;
  hasFransiza: boolean;
  installments: number | null;
  bankAccount: string;
  caseSectionStepFormId: number;
  currency: string;
  currencyRate: number;
  dateCreation: any;
  createdBy: string;
  createdByUser: AppUser | null;
}

export interface CaseServiceAppointmentHistory {
  id: number;
  caseId: number;
  comment: string;
  attachments: CaseAttachment[];
  userId: string;
  caseSectionStepFormId: number;
  serviceInDate: Date;
  serviceOutDate: Date;
}

export interface ValuationVehicle {
  basePrice: number;
  body: string;
  bodyType: string;
  convertedEndSellingDate: Date;
  drivenWheels: string;
  endSellingDate: string;
  engineCode: string;
  fuelType: string;
  manufacturer: string;
  model: string;
  modelYear: number;
  noOfDoors: number;
  powerHP: number;
  kw: number;
  startSellingDate: string;
  transmission: string;
  transmissionDescription: string;
  trimLevel: string;
  vehicleId: number;
  version: string;
  vinQuery: boolean;
  wholesalePrice: number;
  vat: string;
  imageUrl: string;
  fabricationDate?: string;
}

export interface ValuationVehicles {
  vehicles: ValuationVehicle[];
  extraOptionCodes: string[];
  billingHistoryId?: number | null;
}

export interface valuationVehiclesInfoRequest {
  registration_date: string | null;
  ignore_date: boolean;
  override_existing_match: boolean;
  trackBilling?: boolean;
  workflowCaseId?: number;
}

export interface ValuationHistory {
  attritionValue: number;
  bodyDamageValue: number;
  caseId: number;
  createdBy: string;
  date: string;
  dateCreation: string;
  dateModification: string;
  extraOptionsDepreciationPrice: number;
  extraOptionsPrice: number;
  fabricationDate: string;
  gpl: boolean;
  id: number;
  modifiedBy: string;
  numberOfKilometers: number;
  offerPrice: number;
  priceAsNew: number;
  retailPrice: number;
  tradeInPrice: number;
  userId: string;
  valuationDate: string;
  valuationPrice: number;
  valuationSourceId: number;
  vatRate: number;
  vehicleSourceId: number;
  updatedByUserFullName?: string;
  email?: string;
}

export interface ValuationExtraOption {
  description: string;
  optionCode: string;
  optionId: number;
  optionType: string;
  price: number;
  checked: boolean;
}
export interface ValuationStandardOption {
  content: string;
  languageId: number;
  schemaId: number;
}
export interface ValuationVehicleOptions {
  extraOptions: ValuationExtraOption[];
  standardOptions: ValuationStandardOption[];
}

export interface ValuationRequest {
  attritionValue: number;
  bodyDamageValue: number;
  extraOptionsPrice: number;
  gpl: boolean;
  numberOfKilometers: number;
  vehicleId: number;
  priceAsNew: number;
  options: ValuationExtraOption[];
  fabricationDate: string | null;
  registrationDate: string;
  imageUrl: string;
  color?: string;
  vin: string;
  version: string;
  equipmentVersion: string;
  make: string;
  model: string;
  fuelType: string;
  bodyType: string;
  horsePower: number;
  nrOfDoors: number;
  transmissionType: string;
  billingHistoryId?: number | null;
}

export interface ValuationModel {
  currency: string;
  manufacturer: string;
  maxPrice: string;
  minPrice: string;
  model: string;
  imageUrl: string;
}

export interface ValuationBody {
  currency: string;
  manufacturer: string;
  maxPrice: string;
  minPrice: string;
  body: string;
  doorsNo?: number;
  model?: string;
  imageUrl: string;
}

export interface ValuationGeneration {
  currency: string;
  manufacturer: string;
  maxPrice: string;
  minPrice: string;
  body: string;
  doorsNo?: number;
  model?: string;
  imageUrl: string;
  generation: number;
  modelYear: number;
}

export interface ValuationIncludeOptions {
  condition: string;
  rule: string;
}

export interface ValuationExcludeOptions {
  condition: string;
  rule: string;
}

export interface ValuationRequiredOptions {
  condition: string;
  rule: string;
}

export interface CaseValuationHistory {
  id: number;
  caseId: number;
  date: string;
  userId: string;
  valuationSourceId: number;
  vehicleSourceId: number;
  valuationDate: string;
  numberOfKilometers: number;
  extraOptionsPrice: number;
  extraOptionsDepreciationPrice: number;
  valuationPrice: number;
  priceAsNew: number;
  bodyDamageValue: number;
  attritionValue: number;
  fabricationDate: string;
  gpl: boolean;
  dateCreation: string | null;
  dateModification: string | null;
  createdBy: string;
  modifiedBy: string;
  tradeInPrice: number;
  retailPrice: number;
  offerPrice: number;
  vatRate: number;
  color: string;
}

export interface CaseClient {
  id: number;
  caseId: number;
  clientId: number;
  personTypeId: number | null;
  personType: IReferential | null;
  companyNumber: string;
  companyName: string;
  pin: string;
  lastName: string;
  firstName: string;
  contactPerson: string;
  email: string;
  phone: string;
  gdprAgreement: boolean;
  caseSectionStepFormId: number;
  address: string | null | undefined;
  city: string | null | undefined;
  region: string | null | undefined;
  regionId: number | null;
  district: string | null | undefined;
  attachments: CaseAttachment[];
  bankAccount: string;
}

export interface CarDetails {
  id: number;
  caseId: number;
  userId: number;
  user: AppUser | null;
  [key: string]: any;
  plateNumber: string | undefined;
  chasisCode: string | null;
  brand: IReferential | null;
  model: IReferential | null;
  bodyType: IReferential | null;
  version: string | null;
  equipmentVersion: string | null;
  yearOfFabrication: Date;
  registrationDate: Date;
  numberOfKilometers: number | undefined;
  power: number | undefined;
  horsePower: number | undefined;
  cilinderCap: number | undefined;
  fuel: IReferential | null;
  pollutionNorm: IReferential | null;
  transmission: IReferential | null;
  traction: IReferential | null;
  color: string | undefined;
  paintType: IReferential | null;
  tireType: IReferential | null;
  rimType: IReferential | null;
  carInteriorStateType: IReferential | null;
  carExteriorStateType: IReferential | null;
  carMechanicalStateType: IReferential | null;
}

export interface ClientOffer extends VehicleOffer {
  caseClientHistoryId: number | null;
  caseClientHistory: CaseClient | null;
  vehicle: VehicleExt | null;
}

export interface UndocumentedModel {
  isActive: boolean;
  dateModification: string | null;
  modifiedBy: string | null;
  gtId: number | null;
  id: number;
  modelDevelopmentType: string;
  hasVinQuery: boolean;
  code: string;
  brand: string;
  brandId: number;
  name: string;
  displayName: string;
}

export interface CarException {
  id: number;
  plateNumber: string;
  vin: string;
  name: string;
  code: string | null;
  gtId: number | null;
  gtOrg: number | null;
  displayName: string;
  dateCreation: string | null;
  translations: ITranslation[];
  modifiedBy: string | null;
  dateModification: string | null;
  isActive: boolean;
}

export interface CarClassRentPrice {
  id: number;
  carClassId: number;
  carClass: IReferential | null;
  price: number;
  dateCreation: string | null;
  createdBy: string | null;
  dateModification: string | null;
  modifiedBy: string | null;
}

export interface Offer {
  id: number;
  caseId: number;
  price: number;
  offerLines: OfferLine[];
}

export interface OfferLine {
  id: number;
  offerId: number;
  originalPartId: number;
  guideNumber: string;
  quantity: number;
  offerParts: OfferPart[];
}

export interface OfferPart {
  id: number;
  offerLineId: number;
  alternativePartCode: string;
  alternativePartName: string | null;
  alternativePrice: number;
  deliveryTime: string;
  availableQuantity: number;
  rowId: number;
}

export interface BillingFilters {
  startDate: Date | null;
  endDate: Date | null;
  isGtm: boolean;
  language: string;
  countryCode: string;
}

export interface DgpciBillingFilters {
  startDate: Date | null;
  endDate: Date | null;
  isGtm: boolean;
}

export interface VinRequestResponse {
  success: boolean;
  vin: string;
}

export interface PlateNumber {
  PlateNumber: string;
  externalClient: boolean;
}
export interface ClaimServiceRequest {
  caseId: number;
  workflowId: number;
  claimNumber: string;
}

export interface ClaimServiceResponse {
  success: boolean;
  message: string;
  data: string;
}

export interface CalculationDifferencesRequest {
  caseId: number;
  calculationId: number;
  workflowId: number;
}

export interface CalculationDifferencesResponse {
  success: boolean;
  message: string;
  hasDifferences: boolean;
}

export interface ClaimServiceCalculationRequest {
  workflowId: number;
  calculationId: number;
  claimNumber: string;
  requireApproval: boolean;
  serviceId: number;
}

export interface ClaimServiceCalculationResponse {
  success: boolean;
  message: string;
  data: Map<string, string[]> | string;
}

export interface ClaimStatusRequest {
  workflowId: number;
  claimNumber: string;
  statusName: string;
}

export interface ClaimStatusResponse {
  success: boolean;
  message: string;
  data: string;
}

export interface ClaimServiceAlternativeCalculationRequest {
  workflowId: number;
  claimNumber: string;
  originalCalculationId: number;
  alternativeCalculationId: number;
}

export interface ClaimServiceAlternativeCalculationResponse {
  success: boolean;
  message: string;
  data: string;
}

export interface LanguageApi {
  name: string;
  code: string;
  translations: number;
  percentage: number;
  updated: string;
}

export interface LanguageLocal {
  code: string;
  translations: string;
  updated: string;
}

export interface MaintenanceCupiOptionalPart {
  id: number;
  maintenanceOptionId: number;
  maintenanceOption: IReferential | null;
  cupiCode: string;
  dateCreation: string | null;
  createdBy: string | null;
  dateModification: string | null;
  modifiedBy: string | null;
}

export interface ManipulationTariffPercentResponse {
  id: number;
  value: number;
}

export interface CarfixAlternativeCalculationRequest {
  caseId: number;
  calculationId: number;
  workflowId: number;
}

export interface CarfixAlternativeCalculationResponse {
  success: boolean;
  code: string;
  message: string;
  caseStatusHistory: CaseStatusHistory;
}

export interface CarfixAttachmentNotificationRequest {
  caseId: number;
  attachmentId: number;
  attachmentTypeCode: string;
  workflowId: number;
}

export interface CarfixAttachmentNotificationResponse {
  success: boolean;
  code: string;
  message: string;
}

export interface CaseVehicleDetails {
  id: number;
  caseId: number;
  repairDate: string | null;
  date: Date;
  hasOkWindows: boolean;
  hasGlitchWindows: boolean;
  hasWindowsComment: string;
  hasOkLights: boolean;
  hasGlitchLights: boolean;
  hasLightsComment: string;
  hasOkBodyAndPaint: boolean;
  hasGlitchBodyAndPaint: boolean;
  hasBodyAndPaintComment: string;
  hasOkWipers: boolean;
  hasGlitchWipers: boolean;
  hasWipersComment: string;
  fuelInTheTank1: boolean;
  fuelInTheTank2: boolean;
  fuelInTheTank3: boolean;
  fuelInTheTank4: boolean;
  fuelInTheTank5: boolean;
  mandatoryEquipment1: boolean;
  mandatoryEquipment2: boolean;
  mandatoryEquipment3: boolean;
  mandatoryEquipment4: boolean;
  mandatoryEquipment5: boolean;
  otherEquipment1: boolean;
  otherEquipment2: boolean;
  otherEquipment3: boolean;
  otherEquipment4: boolean;
  otherEquipment5: boolean;
  hasPickUp: boolean;
  pickUpCode: string;
  replacementVehicle: boolean;
  documentsReceived1: boolean;
  documentsReceived2: boolean;
  documentsReceived3: boolean;
  documentsReceived4: boolean;
  serviceComment: string;
  repairPrice: number | null;
  appointmentId: number;
  appointment: Appointment;
}

export interface TariffPercentRequest {
  workflowId: number;
  insurerId: number | null;
  tariffCode: string;
  vehicleTypeId: number | null;
  jobTypeId: number | null;
  regionId: number | null;
  serviceId: number | null;
}

export interface TariffPercentResponse {
  id: number;
  value: number;
}

export interface MaintenanceProducer {
  id: number;
  countryId: number;
  country: IReferential | null;
  countryCode: string;
  maintenancePackageId: number;
  maintenancePackage: IReferential | null;
  supplierId: number;
  supplier: Organization | null;
  producer: string;
  dateCreation: string | null;
  createdBy: string | null;
  dateModification: string | null;
  modifiedBy: string | null;
}

export interface VQVehicle {
  id: number;
  model: string;
  make: string;
  umc: string;
  dateCreation: string | null;
  createdBy: string | null;
  dateModification: string | null;
  modifiedBy: string | null;
}

export interface Appointment {
  id: number;
  serviceId: number;
  service: Organization | null;
  parentId: number | null;
  appAppointmentId: number | null;
  date: string;
  appointmentStatus: IReferential;
  appointmentCategoryTypeId: number;
  appointmentCategoryType: IReferential | null;
  vehicle: AppointmentVehicle;
  dateCreation: string | null;
  createdBy: string | null;
  dateModification: string | null;
  modifiedBy: string | null;
  hideCancelButton: boolean;
  vehicleId: number | null;
  driverId: string | null;
  partnerOrganizationId: number | null;
}

export interface AppointmentVehicle {
  id: number;
  appointmentId: number;
  vin: string | undefined;
  plateNumber: string | undefined;
  firstPlateDate: string | undefined;
  registrationDate: string | undefined;
  motDate: string | null;
  carColor: string | undefined;
  numberOfKilometers: number | undefined;
  carCategoryId: number | undefined;
  carClassId: number | undefined;
  power: number | undefined;
  gearboxType: string | undefined;
  engineSize: number | undefined;
  brandId: number | undefined;
  modelId: number | undefined;
  model: Model | null;
  date: Date;
  userId: number;
  user: AppUser | null;
  engineCode: string;
  identifiedMake: string;
  identifiedModel: string;
  identifiedCarBody: string;
  identifiedFuelType: string;
  vehicleTypeId: number | null;
  vehicleType: IReferential | null;
  [key: string]: any;
}

export interface AppointmentFilters {
  isNew: boolean;
  dateFilterStartDate: string;
  dateFilterEndDate: string;
  page: number;
  pageSize: number;
}

export interface AppointmentList {
  appointments: Appointment[];
  total: number;
  page: number;
}

export interface AppointmentListDto {
  Appointments: Appointment[];
  total: number;
  page: number;
}

export interface Appointment {
  id: number;
  serviceId: number;
  service: Organization | null;
  parentId: number | null;
  appAppointmentId: number | null;
  date: string;
  appointmentStatus: IReferential;
  appointmentStatusHistory: AppointmentStatusHistory;
  appointmentCategoryTypeId: number;
  appointmentCategoryType: IReferential | null;
  vehicle: AppointmentVehicle;
  dateCreation: string | null;
  createdBy: string | null;
  dateModification: string | null;
  modifiedBy: string | null;
  partnerOrganizationId: number | null;
}

export interface AppointmentStatusHistory {
  id: number;
  appointmentId: number;
  appointmentStatusId: number;
  appointmentStatus: IReferential | null;
  date: Date;
  userId: string;
  user: AppUser | null;
  assignedToId: string;
  assignedTo: AppUser | null;
  assignedToHoId: number;
  isNew: boolean;
  isViewed: boolean;
  modifiedByClient: boolean;
}

export interface AppointmentVehicle {
  id: number;
  appointmentId: number;
  vin: string | undefined;
  plateNumber: string | undefined;
  firstPlateDate: string | undefined;
  registrationDate: string | undefined;
  motDate: string | null;
  carColor: string | undefined;
  numberOfKilometers: number | undefined;
  carCategoryId: number | undefined;
  carClassId: number | undefined;
  power: number | undefined;
  gearboxType: string | undefined;
  engineSize: number | undefined;
  brandId: number | undefined;
  modelId: number | undefined;
  model: Model | null;
  date: Date;
  userId: number;
  user: AppUser | null;
  engineCode: string;
  identifiedMake: string;
  identifiedModel: string;
  identifiedCarBody: string;
  identifiedFuelType: string;
  vehicleTypeId: number | null;
  vehicleType: IReferential | null;
}

export interface GTWSBillingFilters {
  startDate: Date | null;
  endDate: Date | null;
  isGtm: boolean;
}

export interface AutomateAppointmentRequest {
  caseId: number;
  workflowId: number;
}

export interface AcquisitionCaseRequest {
  vin: string;
  dealerId: number;
}

export interface CaseResultDto {
  id: number;
  caseStatus: IReferential;
  vehicleId: number;
}

export interface ChangeCaseStatusBasedCodeRequest {
  caseId: number;
  statusCode: string;
}

export interface NonStockBillingFilters {
  startDate: Date | null;
  endDate: Date | null;
}

export interface GenerateInvoiceRequest {
  caseId: number;
  workflowId: number;
  template: string;
}

export interface GenerateInvoiceResponse {
  file: Blob;
}

export interface PartsHistoryInterogationRequest {
  calculationId: number;
  language: string;
}

export interface UpdateOrderInfoRequest {
  calculationId: number;
  orderId: number;
  parts: OrderPart[];
}

export interface AppointmentPaymentStatusRequest {
  workflowId: number;
  caseId: number;
}

export interface AppointmentPaymentStatusResponse {
  id: number;
  pre_appointment: PreAppointment;
}

export interface PreAppointment {
  id: number;
  payment: PaymentDto;
}

export interface PaymentDto {
  amount: number | null;
  currency: string;
  type: string;
  status: string;
  description: string;
}

export interface SendOrderNotificationRequest {
  orderId: number;
  orderComment: string;
  caseId: number;
  email: string;
  template: string;
}

export interface SendAlternativeCalculationRequest {
  workflowId: number;
  caseNumber: string;
  vin: string;
  alternativeCalculationId: number;
}

export interface SendAlternativeCalculationResponse {
  success: boolean;
  message: string;
}

export interface CreatePartsToBeOrderedRequest {
  workflowId: number;
  automateGenerateOrder: boolean;
  partStock: boolean;
  nonStockOrder: boolean;
}

export interface NonStockPart {
  vin: string;
  make: string;
  model: string;
  oeCode: string;
  oeSupplierCode: string;
  supplierId: number;
}

export interface PartNotifySuppportRequest {
  caseId: number;
  originalPart: OriginalPart;
  template: string;
  organizationOwnerId: number;
}

export interface OriginalPart {
  oeCode: string;
  name: string;
  price: number;
  quality: string;
  comment: string;
}

export interface PartNotifySuppportResponse {
  success: boolean;
}

export interface SendCaseNotificationRequest {
  workflowId: number;
  caseId: number;
  caseStatusCode: string;
  organizationId: number;
  template: string;
}

export interface SendCaseNotificationResponse {
  success: boolean;
  message: string;
  caseStatusHistory: CaseStatusHistory;
}

export interface NotificationDetailsDto {
  id: number;
  statusId: number;
  statusDisplayName: string;
  statusDate: Date;
  categoryDisplayName: string;
  vehiclePlateNumber: string;
  organizationOwnerName: string;
  caseNumber: string;
}

export interface CaseNewInNotificationItemDto {
  type: 'caseNewIn';
  dateCreated: Date;
  entity: UserNotification;
  isInPopup: boolean;
}

export interface AppointmentNotificationItemDto {
  type: 'appointment';
  dateCreated: Date;
  entity: UserNotification;
  isInPopup: boolean;
}

export interface TaskNotificationItemDto {
  type: 'task';
  dateCreated: Date;
  entity: UserNotification;
  isInPopup: boolean;
}

export interface CommentNotificationItemDto {
  type: 'comment';
  dateCreated: Date;
  entity: UserNotification;
  isInPopup: boolean;
}

export interface MavoNotificationItemDto {
  type: 'mavo';
  dateCreated: Date;
  entity: UserNotification;
  isInPopup: boolean;
}

export type NotificationItem =
  | CaseNewInNotificationItemDto
  | AppointmentNotificationItemDto
  | TaskNotificationItemDto
  | CommentNotificationItemDto
  | MavoNotificationItemDto;

export interface NotificationProps {
  onClick?: () => void;
  item: NotificationItem;
  index: number;
}

export type UserNotificationProps = { appState: AppState } & typeof AppActionCreators &
  RouteComponentProps &
  NotificationProps;

export interface DeletePartToBeOrderedRequestDto {
  caseId: number;
}

export interface CaseInfo {
  id: number;
  hasRemainingNonStockParts: boolean;
  isOffline: boolean;
  partnerOrganizationId: number | null;
  isInvoicedByGTM: boolean;
  skipGeneratingDocuments: boolean;
  isGtmManaged: boolean;
  alternativePartVariantsEnabled: boolean;
  approvedCalculationId: number | null;
  serviceId: number | null;
}

export interface GeneralNotifySupportRequest {
  template: string;
  client: NotificationClient;
}

export interface NotificationClient {
  lastName: string;
  firstName: string;
  companyName: string;
  companyId: number;
  email: string;
  phoneNumber: string;
  caseId: number | null;
  comment: string;
}

export interface GeneralNotifySupportResponse {
  success: boolean;
  message: string;
}

export interface NotifySupportRequest {
  caseId: number;
  toUserId: string;
  caseComment: CaseComment;
  template: string;
}

export interface NotifySupportResponse {
  success: boolean;
  message: string;
}

export interface NewBillingReportFilters {
  startDate: Date | null;
  endDate: Date | null;
  isGtm: boolean;
  language: string;
}

export interface PartOptionCupiCode {
  id: number;
  partOptionId: number;
  partOption: IReferential | null;
  countryId: number;
  country: IReferential | null;
  cupiCode: string;
  dateCreation: string | null;
  createdBy: string | null;
  dateModification: string | null;
  modifiedBy: string | null;
}

export interface TopProducer {
  id: number;
  countryId: number;
  country: IReferential | null;
  producer: string;
  dateCreation: string | null;
  createdBy: string | null;
  dateModification: string | null;
  modifiedBy: string | null;
}

export interface CasePartSelection {
  id: number;
  caseId: number;
  partSelectionOptionId: number | null;
  partSelectionOption: IReferential | null;
  casePartSelectionOptions: CasePartSelectionOption[];
}

export interface CasePartSelectionOption {
  partOptionId: number;
}

export interface CreatePackageOfferResponse {
  success: boolean;
  originalCalculationId: number | null;
  alternativeCalculationId: number | null;
  gtEstimateResponseDto: GtEstimateResponseDto;
  excludedOptions: ExcludedOptionDto[];
}

export interface GtEstimateResponseDto {
  error: GtEstimateError;
}

export interface GtEstimateError {
  errorInfo: ErrorInfo;
}

export interface ExcludedOptionDto {
  id: number;
  code: string;
  displayName: string;
}

export interface DeleteVQDataResponseDto {
  success: boolean;
  message: string;
}

export interface CreatePackageOfferRequestDto {
  caseId: number;
  serviceId: number;
  workflowId: number;
  countryId: number;
  vehicleTypeId: number | null;
  jobTypeId: number | null;
  casePartSelection: CasePartSelection;
}

export interface OrderSummary {
  id: number;
  calculationTypeId: number;
  calculationType: IReferential | null;
  caseId: number;
  date: string;
  currencyCode: string;
  amount: number;
  amountWithoutTVA: number;
  amountTVA: number;
  partsLocationOrganizationId: number | null;
  parts: OrderSummaryPart[];
}

export interface OrderSummaryPart {
  id: number;
  partGroup: string;
  caseCalculationHistoryId: number;
  calculationSectionId: number;
  calculationSection: IReferential;
  guideNumber: string;
  repairMethod: string;
  pieceNr: number;
  originalPrice: number;
  name: string;
  alternativeGuideNumber: string;
  alternativePartName: string;
  supplierId: number | null;
  supplier: Organization | null;
  producer: string;
  deliveryTime: string;
  discount: number | null;
  alternativePartDiscount: number | null;
  alternativePrice: number | null;
  alternativePartQualityId: number | null;
  totalPrice: number;
  alternativePartQuality: IReferential | null;
  alternativePartStockQuantity: number | null;
  alternativePartStockLocation: string;
  technicianCode: string;
  auxiliarInformation: string;
  availableQuantity?: number | null;
  volumeLiters?: number | null;
}

export interface CaseAttachmentsPublish {
  caseAttachments: CaseAttachment[];
  attachmentTypeCodesOrder: string[];
}

export interface CreateAcquisitionCaseRequest {
  workflowId: number;
  dealerId: number;
  vehicleId: number;
}

export interface InvoicePaymentConfirmationRequest {
  appointmentId: number;
}

export interface InvoicePaymentConfirmationResponse {
  success: boolean;
  message: string;
}

export interface GTPartMapping {
  id: number;
  organizationId: number;
  gtPartId: number;
  gtPart: IReferential | null;
  cupiCodes: string | null;
  operationId: number | null;
  operation: IReferential | null;
  requiresDamageLevel: boolean;
  dateCreation: string | null;
  createdBy: string | null;
  dateModification: string | null;
  modifiedBy: string | null;
}

export interface GTPartMappingImportRequestDto {
  attachments: ImportGTPartMappingAttachment[];
  organizationId: number;
}

export interface ImportGTPartMappingAttachment {
  id: number;
  fileName: string;
  uploadDate: Date;
  file: File;
  originalFileName: string;
}

export interface GTPartMappingImportedDto {
  id: number;
  organizationId: number;
  gtPartId: number;
  gtPartName: string;
  cupiCodes: string;
  operationId: number;
  operationName: string;
  requiresDamageLevel: boolean;
  importedMessage: string;
}

export interface GTPartMappingImportReponseDto {
  importedNumber: number;
  totalNumber: number;
  gtPartMappingList: GTPartMappingImportedDto[];
}

/** Mirrors ClaimBox.Common.DTO.CreateInvoiceResultDto */
export interface CreateInvoiceResultDto {
  id: number;
  relatedEntityType: string | null;
  relatedEntityId: number;
  paymentId: number | null;
  organizationId: number;
  issuerOrganizationId: number;
  invoiceDate: string;
  dueDate: string | null;
  invoiceNumber: string | null;
  statusId: number;
  vatValue: number;
  amount: number;
  vatAmount: number;
  calculationId: number | null;
  anafStatusId: number | null;
  anafIndexUpload: string | null;
  anafDownloadId: string | null;
  currency: string | null;
  originalFileName: string | null;
  fileName: string | null;
  annexId: number | null;
  annex: InvoiceAnnexDto | null;
  dateCreation: string | null;
  dateModification: string | null;
  createdBy: string | null;
  modifiedBy: string | null;
}

/** Mirrors Case.Domain.Entities.InvoiceAnnex */
export interface InvoiceAnnexDto {
  id: number;
  annexDate: string;
  number: string;
  originalFileName: string;
  fileName: string;
  statusId: number;
  dateCreation: string | null;
  dateModification: string | null;
  createdBy: string | null;
  modifiedBy: string | null;
}

export interface InvoiceReportResult {
  invoiceContentBase64: string;
  invoice: CreateInvoiceResultDto;
  caseAttachments: CaseAttachment[];
}

export interface StockPart {
  id: number;
  organizationId: number;
  guideNumber: string;
  alternativeGuideNumber: string;
  alternativePartName: string;
  alternativePrice: number;
  alternativePartQualityId: number | null;
  alternativePartQualityName: string;
  alternativePartQuality: IReferential | null;
  deliveryTime: string;
  producer: string;
  supplierName: string;
  availableQuantity: number;
  alternativePartStockLocation: string;
  dateCreation: string | null;
  createdBy: string | null;
  dateModification: string | null;
  modifiedBy: string | null;
}

export interface StockPartImportRequestDto {
  attachments: ImportStockPartAttachment[];
  organizationId: number;
}

export interface ImportStockPartAttachment {
  id: number;
  fileName: string;
  uploadDate: Date;
  file: File;
  originalFileName: string;
}

export interface StockPartImportedDto {
  id: number;
  organizationId: number;
  supplinerName: string;
  guideNumber: string;
  alternativeGuideNumber: string;
  alternativePartName: string;
  alternativePrice: number;
  alternativePartQualityId: number | null;
  alternativePartQuality: string;
  deliveryTime: string;
  producer: string;
  supplierName: string;
  availableQuantity: number;
  alternativePartStockLocation: string;
  importMessage: string;
}

export interface StockPartImportResponseDto {
  importedNumber: number;
  totalNumber: number;
  stockPartList: StockPartImportedDto[];
}

export interface GetSuppliersAlternativePartsRequestDto {
  partNumber: string;
  partId: number;
  addition: number;
  discount: number;

  workflowId: number;
  organizationId: number | null;
  supplierId?: number | null;

  stockPartInterogation: boolean;
  organizationOwnerId: number | null;
  qty: number;
}

export interface MaintenanceCategoryCountry {
  id: number;
  maintenanceCategoryId: number;
  maintenanceCategory: IReferential | null;
  countryId: number;
  country: IReferential | null;
  isActive: boolean;
  dateCreation: string | null;
  createdBy: string | null;
  dateModification: string | null;
  modifiedBy: string | null;
  maintenanceCategoryOptions: MaintenanceCategoryOption[];
}

export interface MaintenanceCategoryOption {
  id: number;
  maintenanceOptionId: number;
  maintenanceOption: IReferential | null;
  maintenanceCategoryCountryId: number;
  maintenanceCategoryCountry: MaintenanceCategoryCountry | null;
  isActive: boolean;
  dateCreation: string | null;
  createdBy: string | null;
  dateModification: string | null;
  modifiedBy: string | null;
}

export interface LastApprovedCalculationRequestDto {
  caseId: number;
  calculationTypeCode: string;
}

export interface LastApprovedCalculationResponseDto {
  hasUnorderedParts: boolean;
  calculationHistory: CaseCalculationHistory | null;
}

export interface UpdateCaseCalculationPartRequestDto {
  parts: CaseCalculationPart[];
  comment: string;
  offerNumber: string;
  partsLocationOrganizationId: number | null;
  partsOrganizationId: number | null;
  organizationId: number | null;
  organizationName: string | null;
  isUpdate: boolean;
  manipulationPercent: number;
  additionInsurer: number | null;
  formId: number;
  interogatePartner: boolean;
  hasUnorderedParts: boolean;
  excludeGTMAddition: boolean;
}

export interface UpdateCalculationPartsResponseDto {
  id: number;
}

export interface BuildOrderedCalculationPartsResult {
  caseCalculationPartsCopy: CaseCalculationPart[];
  calculationPartsOrdered: CaseCalculationPart[];
}

export interface DealerFlowFormProps {
  workflowForm: WorkflowSectionStepForm;
  caseSettingsState: CaseSettingsState;
  appState: AppState;
}

export interface ValuationBillingFilters {
  startDate: Date | null;
  endDate: Date | null;
  isGtm: boolean;
  language: string;
}

export interface QuantityWarningInfo {
  quantity: number;
}

export interface AdditionDto {
  fixedValue: number;
  percentageValue: number;
  marginValue: number;
}

export interface CreateInvoiceRequestDto {
  id: number;
  invoiceTypeId: number;
  relatedEntityType: string;
  relatedEntityId: number;
  paymentId: number | null;
  organizationId: number;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string | null;
  vatValue: number;
  amount: number;
  vatAmount: number;
  amountWithoutVat: number;
  currency: string;
  originalFileName: string;
  calculationId: number | null;
  issuerOrganizationId: number;
  paymentMethodId: number | null;
  paymentDays: number | null;
  file: File | null;
}

export interface AddInvoiceCaseAttachmentRequestDto {
  caseId: number;
  attachmentTypeId: number;
  attachmentTypeCode: string;
  invoiceNumber: string;
  invoiceDate: string;
  amount: number;
  amountWithoutVat: number;
  vatValue: number;
  vatAmount: number;
  issuerOrganizationId: number;
  currency: string;
  file: File | null;
}

export interface AddInvoiceCaseAttachmentResponseDto {
  invoice: InvoiceResponseDto;
  caseAttachments: CaseAttachment[];
}

export interface GetRepairServiceToGTMInvoicedCasesRequestDto {
  startDate: Date | null;
  endDate: Date | null;
  language: string;
}

export interface GetLastInvoiceRequestDto {
  caseId: number;
  invoiceType: number;
}

export interface InvoiceResponseDto {
  id: number;
  invoiceTypeId: number;
  relatedEntityId: number;
  relatedEntityType: string;
  paymentId: number | null;
  organizationId: number;
  issuerOrganizationId: number;
  invoiceDate: string;
  dueDate: string | null;
  invoiceNumber: string;
  statusId: number;
  vatValue: number;
  amount: number;
  vatAmount: number;
  amountWithoutVat: number;
  currency: string;
  originalFileName: string;
  fileName: string;
  calculationId: number | null;
  anafStatusId: number | null;
  anafIndexUpload: string;
  anafDownloadId: string;
}

export type CreateGTMCalculationCopyRequestDto = {
  calculationId: number;
  serviceId: number;
  workflowId: number;
};

export type CreateGTMCalculationCopyResponseDto = {
  id: number;
  currencyCode: string;
  amount: number;
  amountWithoutTVA: number;
  amountTVA: number;
  serviceCalculationId: number | null;
};

export type GTMCalculationCopyResultDto = {
  hasError: boolean;
  calculationId: number;
};

export type TrimCompositeDto = {
  trim: string;
  composite: number | null;
};

export type ChosenVehicleOptionTotalsDto = {
  standard_schema_ids: number;
  extra_option_ids: number;
  mapped_extra_option_ids: number;
};

export type ChosenVehicleOptionIdsDto = {
  chosen_vehicle_id: number;
  standard_schema_ids: number[];
  extra_option_ids: number[];
  mapped_extra_option_ids: number[];
  totals: ChosenVehicleOptionTotalsDto | null;
};

export type GtTrimSelectionDto = {
  alternative_trims: TrimCompositeDto[];
};

export type CoverageStatsDto = {
  trim_confidence: string | null;
};

export type JatoResultDto = {
  vehicle_id: number;
  vehicle_code: number | null;
  make: string | null;
  model: string | null;
  model_generation: number | null;
  model_descriptor: string | null;
  vehicle_type: string | null;
  make_group: string | null;
  model_year: string | null;
  version_name: string | null;
  local_version_name: string | null;
  trim_level: string | null;
  body_style: string | null;
  segment_global: string | null;
  segment_local: string | null;
  fuel_type: string | null;
  powertrain: string | null;
  power_hp: number | null;
  secondary_fuel_type: string | null;
  power_kw: number | null;
  thermal_kw: number | null;
  electric_kw: number | null;
  engine_code: string | null;
  gross_mass: number | null;
  cc: number | null;
  co2: number | null;
  fuel_consumption_combined: number | null;
  power_consumption_combined: number | null;
  number_of_doors: string | null;
  transmission: string | null;
  drive_type: string | null;
  pollution: string | null;
  start_selling_date: string | null;
  end_selling_date: string | null;
  country_of_assembly: string | null;
  base_price: number | null;
  wholesale_price: number | null;
  production_year: number | null;
};

export type AiSearchDto = {
  error: boolean;
  message: string | null;
  results: JatoResultDto[];
  total: number;
  result_mode: string | null;
  description: string | null;
  chosen_vehicle_option_ids: ChosenVehicleOptionIdsDto | null;
  gt_trim_selection: GtTrimSelectionDto | null;
  coverage_stats: CoverageStatsDto | null;
};

export type CarfixJatoIdentificationResponse = {
  error: boolean;
  message: string | null;
  env: string | null;
  source_info: any;
  source_used: any;
  source_response: string | null;
  data_provider: string | null;
  ai_search: AiSearchDto | null;
};

export type CarfixJatoIdentificationRequestDto = {
  vin: string;
  providers: string[];
  organizationId: number;
  organizationName: string;
};

export type CarfixJatoIdentificationResponseDto = {
  error: boolean;
  message: string | null;
  env: string | null;
  source_info: any;
  vehicles: any[];
  carfixResponse: CarfixJatoIdentificationResponse | null;
  billingHistoryId: number | null;
};

export type GetCaseAttachmentsRequestDto = {
  caseId: number;
  types: string[];
  viewHiddenServiceDocuments?: boolean;
};

export type GetCaseAttachmentsArchiveRequestDto = {
  caseId: number;
  types: string[];
  formId: number | null;
  viewHiddenServiceDocuments?: boolean;
};

export type GetCaseAppointmentAttachmentsRequestDto = {
  caseId: number;
  appointmentId: number;
  types: string[];
};

export type GetAppointmentAttachmentsRequestDto = {
  appointmentId: number;
  types: string[];
};

export interface VehicleVQRequery {
  id: number;
  vin: string;
  isActive: boolean;
  dateCreation: string | null;
  dateModification: string | null;
  createdBy: string | null;
  modifiedBy: string | null;
}

export interface VehicleVQRequeryImportRequestDto {
  attachments: ImportVehicleVQRequeryAttachment[];
}

export interface ImportVehicleVQRequeryAttachment {
  id: number;
  fileName: string;
  uploadDate: Date;
  file: File;
  originalFileName: string;
}

export interface VehicleVQRequeryImportReponseDto {
  importedNumber: number;
  totalNumber: number;
  vehicleVQRequeryList: VehicleVQRequeryImportedDto[];
}

export interface VehicleVQRequeryImportedDto {
  id: number;
  vin: string;
  importMessage: string;
}

export interface RepoVehicleFilterRequestDto {
  organizationIds: number[];
  brands: string[];
  models: string[];
  vin: string | null;
  page: number;
  pageSize: number | null;
  paginate: boolean;
  language: string;
}

export interface RepoVehicleFilterResponseDto {
  vehicles: RepoVehicleDto[];
  total: number;
  page: number;
}

export interface RepoVehicleFiltersDto {
  organizations: OrganizationInfoDto[];
}

export interface RepoVehicleDto {
  id: number;
  vin: string;
  model: string;
  make: string;
  umc: string;
  organizationId: number | null;
  organization: OrganizationInfoDto | null;
  dateCreation: string | null;
  dateModification: string | null;
  createdBy: string | null;
  modifiedBy: string | null;
}

export type RepairServiceInvoiceContext = {
  attachmentTypeId: number;
  attachmentTypeCode: string;
  vatValue: number | null;
  currency: string;
};

export interface GTSupplierMapping {
  id: number;
  categoryId: number;
  category?: IReferential | null;
  brandId: number;
  brand?: IReferential | null;
  cupiCode: string;
  gtCode: string;
  partQualityId: number;
  partQuality?: IReferential | null;
  associatedCode: string;
  volumeLiters: number | null;
  price: number | null;
  modelDescription: string | null;
  gearboxCode: string | null;
  differentialCode: string | null;
  dateCreation: string | null;
  dateModification: string | null;
  createdBy: string | null;
  modifiedBy: string | null;
}

export interface GTSupplierMappingImportRequestDto {
  attachments: ImportGTSupplierMappingAttachment[];
}

export interface ImportGTSupplierMappingAttachment {
  id: number;
  fileName: string;
  uploadDate: Date;
  file: File;
  originalFileName: string;
}

export interface GTSupplierMappingImportResponseDto {
  importedNumber: number;
  totalNumber: number;
  gtSupplierMappingList: GTSupplierMappingImportedDto[];
}

export interface GTSupplierMappingImportedDto {
  id: number;
  categoryId: number;
  category: string;
  brandId: number;
  brand: string;
  cupiCode: string;
  gtCode: string;
  partQualityId: number;
  partQuality: string;
  associatedCode: string;
  volumeLiters: number | null;
  price: number;
  modelDescription: string | null;
  gearboxCode: string | null;
  differentialCode: string | null;
  importMessage: string;
}

export interface GTSupplierMappingFilterRequestDto {
  categoryIds: number[];
  brandIds: number[];
  qualityIds: number[];
  cupiCode: string | null;
  gtCode: string | null;
  associatedCode: string | null;
  modelDescription: string | null;
  gearboxCode: string | null;
  differentialCode: string | null;
  page: number;
  pageSize: number | null;
  language: string;
}

export interface GTSupplierMappingFilterResponseDto {
  gtSupplierMappings: GTSupplierMapping[];
  total: number;
  page: number;
}

export interface GetGTSupplierMappingRequestDto {
  vin: string | null;
  brandId: number;
  gtCode: string | null;
  qualityId: number | null;
}

export interface GTSupplierMappingDto {
  id: number;
  categoryId: number;
  brandId: number;
  cupiCode: string | null;
  gtCode: string | null;
  partQualityId: number;
  associatedCode: string;
  volumeLiters: number | null;
  price: number;
  modelDescription: string | null;
  gearboxCode: string | null;
  differentialCode: string | null;
}

export interface GTSupplierMappingDuplicateRequestDto {
  id: number;
  categoryId: number;
  brandId: number;
  cupiCode: string | null;
  gtCode: string | null;
  partQualityId: number;
  associatedCode: string;
  modelDescription: string | null;
  gearboxCode: string | null;
  differentialCode: string | null;
}

export interface GTSupplierMappingDuplicateResponseDto {
  isDuplicate: boolean;
  gtSupplierMapping: GTSupplierMappingDto | null;
}

export type GenerateReportAttachmentRequestDto = {
  caseId: number;
  calculationId: number;
  code: string;
  isClientOffer: boolean;
  parameters: ReportParameter[];
  attachmentTypeId: number;
  attachmentTypeCode: string;
  originalFileName: string;
  includeInOtherTab: boolean;
  isHiddenFromClientApp: boolean;
  isHiddenFromServiceApp: boolean;
};

export interface PackageSelectionAlternativePartDto {
  id: number;
  name: string | null;
  alternativeGuideNumber: string | null;
  alternativePrice: number;
  producer: string | null;
  supplierId: number | null;
  isInStock: boolean;
  availableQuantity: number | null;
  expectedDeliveryDate: string | null;
}

export interface PackageSelectionPartDto {
  id: number;
  name: string;
  guideNumber: string;
  pieceNr: number;
  alternativePartList: PackageSelectionAlternativePartDto[];
}

export interface GetPackagePartSelectionsRequestDto {
  parts: PackageSelectionPartDto[];
  countryCode: string;
}

export interface PackagePartSelectionDto {
  package: IReferential;
  isServiceRecomandation: boolean;
  alternativePart: PackageSelectionAlternativePartDto | null;
}

export interface PackagePartSelectionResultDto {
  id: number;
  name: string;
  guideNumber: string;
  pieceNr: number;
  selections: PackagePartSelectionDto[];
}

// Subset of BE NotificationTemplateEnum used for the service verification decision
export enum NotificationTemplateEnum {
  FLEET_OFFER_SERVICE_APPROVED = 'FLEET_OFFER_SERVICE_APPROVED',
  FLEET_OFFER_SERVICE_REJECTED = 'FLEET_OFFER_SERVICE_REJECTED'
}

export interface VerifyFleetCalculationRequestDto {
  calculationId: number;
  template: NotificationTemplateEnum;
}

export interface AlternativeCalculationDialogState {
  caseCalculationHistoryDetails: CaseCalculationHistory;
  isLoading: boolean;
  workflowCalculation: WorkflowCalculation | null;
  workflowOptimization: WorkflowOptimization[];
  openMenuItem: any | null;
  TVA: string;
  calculationSectionList: IReferential[];
  calculationPartsOrdered: CaseCalculationPart[];
  packageCalculationParts: CaseCalculationPart[];
  orderPartList: OrderPart[];
  partQualityTypes: IReferential[];
  maintenancePackages: IReferential[];
  supplierList: Organization[];
  nonStockSuppliers: WorkflowSupplier[];
  partSection: IReferential | null;
  caseStatuses: IReferential[];
  ownStockOrganization: Organization | null;
  organizationBranches: OrganizationBranch[];
  selectedOrganizationBranch: OrganizationBranch | null;
  currentWorkflowContainsOrderFormOrInterogatePartner: boolean;
  hasBillingForm: boolean;
  carClassRentPrice: CarClassRentPrice | null;
  rentVehiclePrices: VehicleDto | null;
}

// XLSX import row shapes (sheet_to_json with named headers). A cell is a raw Excel value.
export type ImportCell = string | number;

export interface OriginalPartRow {
  code: ImportCell;
  name: ImportCell;
  price: ImportCell;
  quality: ImportCell;
  units: ImportCell;
}

export interface AlternativePartRow {
  originalCode: ImportCell;
  alternativeCode: ImportCell;
  name: ImportCell;
  price: ImportCell;
  quality: ImportCell;
  producer: ImportCell;
  supplier: ImportCell;
  delivery: ImportCell;
  availableQuantity: ImportCell;
  location: ImportCell;
  remarks: ImportCell;
}

export type PartitionedCalculationParts = {
  calculationPartsCopy: CaseCalculationPart[];
  selectedCalculationPart: CaseCalculationPart | undefined;
};

export interface FleetCalculationInfoDto {
  fleetCalculation: CaseCalculationHistory | null;
  serviceCalculationId: number | null;
}

export interface DamagePart {
  id: number;
  caseId: number;
  cupiId: number;
  cupiCode: string | null;
  partName: string | null;
  pointsCoordinates: string | null;
  templateImageUrl: string | null;
  damageTypeCupiCommentId: number;
  damageTypeCode: string | null;
  damageTypeDescription: string | null;
  damageTypePvcAction: string | null;
  damageTypePvcReplacement: number | null;
  damageTypePaint: string | null;
  damageTypeTime: number | null;
  damageTypeMaterials: string | null;
  damageTypeOperationDescription: string | null;
  damageLevelId: number | null;
  damageLevel: IReferential | null;
}

export interface FnolDamageTypeDto {
  cupiCommentId: number;
  code: string;
  description: string;
  pvcAction: string | null;
  pvcReplacement: number | null;
  paint: string | null;
  time: number | null;
  materials: string | null;
  operationDescription: string;
  damageLevelId: number | null;
  damageLevel: IReferential | null;
  damageSeverity: FnolDamageSeverityDto | null;
}

export interface FnolDamagePartDto {
  id: number;
  caseId: number;
  cupiId: number;
  cupiCode: string;
  partName: string;
  pointsCoordinates: string | null;
  templateImageUrl: string | null;
  damageType: FnolDamageTypeDto;
  photos: CaseAttachment[];
}

export interface GetDamagePartsResponseDto {
  damagedParts: FnolDamagePartDto[];
}

export interface DuplicateCaseCheckRequestDto {
  vin: string;
  deliveryOrganizationId: number;
}

export interface DuplicateCaseCheckResponseDto {
  id: number;
  caseTypeId: number;
  organizationOwnerId: number;
  createdBy: string;
  dateCreation: Date;
  caseVehicleHistory: CaseVehicleHistory[];
  caseEventDetailsHistory: CaseEventHistory[];
}

export interface FnolDamageSeverityDto {
  damageSv: number;
  nameKey: string;
  descriptionKey: string;
  name: string;
  description: string;
}
